import { PlusIcon } from "@heroicons/react/24/outline";
import { type ColumnDef, flexRender, type Table } from "@tanstack/react-table";
import { PrimaryButton } from "../../ui/Buttons";
import { CustomCheckbox } from "../../ui/CustomCheckbox";
import { TableFooter } from "./DataTableV3";
import { TableHeader } from "./TableHeader";

type WithId = { id: string | number };

export interface DataTableV3UIProps<T extends WithId> {
    table: Table<T>;
    dataName: string;
    columns: ColumnDef<T, any>[];
    loadingData: boolean;
    itemCount: number;
    onClickRow?: (id: T["id"], originalRow?: T) => void;
    createRow?: () => void;
    defaultPageSize?: number;
}

/**
 * Pure presentational component for DataTableV3.
 * Receives all data and callbacks as props, contains no business logic.
 * Can be replaced with alternative UI implementations while using the same hook.
 */
export const DataTableV3UI = <T extends WithId>(
    props: DataTableV3UIProps<T>,
) => {
    const {
        table,
        dataName,
        columns,
        loadingData,
        itemCount,
        onClickRow,
        createRow,
        defaultPageSize,
    } = props;

    return (
        <div className="flex-grow flex flex-col text-base overflow-x-scroll">
            <div className="min-h-56 max-w-full">
                <table className="table-auto w-full">
                    <TableHeader
                        dataName={dataName}
                        headers={table.getHeaderGroups()}
                        table={table}
                    />

                    {/* Table body */}
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                onClick={() => {
                                    if (onClickRow) {
                                        onClickRow(
                                            row.original.id,
                                            row.original,
                                        );
                                    }
                                }}
                                className={`
                                    border-y border-y-ae-neutral-200 hover:bg-ae-neutral-100
                                    h-14 group/tableRow
                                    ${row.getIsSelected() ? "bg-slate-50" : ""}
                                    ${onClickRow ? "hover:cursor-pointer" : ""}
                                `}
                            >
                                {table.options.enableRowSelection && (
                                    <td
                                        className="px-4 text-sm font-normal text-ae-blue-900"
                                        style={{ width: 10 }}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyUp={(e) => e.stopPropagation()}
                                    >
                                        <CustomCheckbox
                                            checked={
                                                row.getIsSelected()
                                                    ? "yes"
                                                    : "no"
                                            }
                                            onClick={(_value, event) =>
                                                row.getToggleSelectedHandler()(
                                                    event,
                                                )
                                            }
                                        />
                                    </td>
                                )}
                                {row.getVisibleCells().map((cell) => (
                                    <td
                                        key={cell.id}
                                        className="py-3 px-4 text-sm font-normal text-ae-blue-900"
                                        style={{
                                            width:
                                                cell.column.getSize() ===
                                                Number.MAX_SAFE_INTEGER
                                                    ? "auto"
                                                    : cell.column.getSize(),
                                            maxWidth: cell.column.getSize(),
                                        }}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {createRow && (
                            <td
                                colSpan={columns.length}
                                className="w-full border-y border-y-ae-neutral-200 h-14"
                            >
                                <div className="w-full flex items-center justify-center">
                                    <PrimaryButton onClick={createRow}>
                                        <PlusIcon className="w-4" />
                                        Add new
                                    </PrimaryButton>
                                </div>
                            </td>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Table footer area */}
            <TableFooter
                loadingData={loadingData}
                currentPage={table.getState().pagination.pageIndex + 1}
                pageCount={table.getPageCount()}
                itemCount={itemCount}
                previousPage={() => table.previousPage()}
                previousPageEnabled={table.getCanPreviousPage()}
                nextPage={() => table.nextPage()}
                nextPageEnabled={table.getCanNextPage()}
                onSelectPageSize={(e) => {
                    table.setPageSize(Number(e.target.value));
                    table.setPageIndex(0);
                }}
                onPageChange={(p) => table.setPageIndex(p - 1)}
                defaultPageSize={defaultPageSize}
                selectedPageSize={table.getState().pagination.pageSize}
            />
        </div>
    );
};
